
import { textSearch } from "@/assets/ts/_utils";
import { toLocaleDateString } from "@/assets/ts/_utils/formatters";
import JsonExcel from "@/components/widgets/tables/JsonExcel.vue";
import { SortProps, sortFn } from "@/core/helpers/sort";
import { Curso } from "@/store/models";
import moment from "moment";
import { computed, defineComponent, ref, watch } from "vue";
import { useArrayPagination } from "vue-composable";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

export default defineComponent({
  name: "cursos-listado",
  components: { "download-excel": JsonExcel },
  props: {
    cursos: Object as () => Curso[],
  },
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    const csvLabels = {
      nombre: t("csv.listados.cursos.nombre"),
      descripcion: t("csv.listados.cursos.descripcion"),
      areaTematica: t("csv.listados.cursos.areaTematica"),
      inicioCSV: t("csv.listados.cursos.inicio"),
      plazas: t("csv.listados.cursos.plazas"),
      inscripcionesActivas: t("csv.listados.cursos.inscripciones"),
    };

    const csvFields = [
      "nombre",
      "descripcion",
      "areaTematica",
      "inicioCSV",
      "plazas",
      "inscripcionesActivas",
    ];
    const csvName = "cursosListado" + new Date().getTime() + ".xlsx";

    const search = ref("");

    const handleEdit = (index: number, row: { id: string }) => {
      router.push(`/listados/cursos/${row.id}`);
    };

    watch(
      () => props.cursos,
      () => {
        currentPage.value = 1;
      }
    );

    const cursos = computed(() => {
      return props.cursos?.map((ele) => {
        return {
          ...ele,
          modalidad:
            ele.videoconferencia && ele.presencial
              ? "Aula Virtual / Presencial"
              : ele.videoconferencia
              ? "Aula Virtual"
              : "Presencial",
          inicioCSV: moment(ele.inicio).format("YYYY-MM-DD HH:mm:ss"),
          inscripciones: ele.inscripciones?.length ?? 0,
          inscripcionesActivas:
            ele.inscripciones?.reduce((contador, inscripcion) => {
              if (
                inscripcion.estado === "INSCRITO" ||
                inscripcion.estado === "FINALIZADO"
              ) {
                return contador + 1;
              } else {
                return contador;
              }
            }, 0) ?? 0,
        };
      });
    });

    const sortProps = ref<SortProps>({
      prop: "createdOn",
      order: "descending",
      method: null,
    });

    const currentYear = new Date().getFullYear();

    const selectedYear = ref(currentYear.toString());
    const startYear = 2022; // Primer servicio es de este año
    const years: string[] = [];
    years.push((currentYear + 1).toString());
    for (let year = currentYear; year >= startYear; year--) {
      years.push(year.toString());
    }
    years.push(t("tabla.serviciosPrestadosListado.anoEjercicioTodos"));

    const filteredCollection = computed(() =>
      (cursos.value ?? [])
        .slice()
        .sort(sortFn(sortProps))
        .filter(
          (data) =>
            !search.value ||
            textSearch(data.nombre, search.value) ||
            textSearch(data.descripcion, search.value) ||
            textSearch(data.modalidad, search.value)
        )
    );

    const cursosListado = computed(() => {
      const filteredBySearch = (cursos.value ?? [])
        .slice()
        .sort(sortFn(sortProps))
        .filter(
          (data) =>
            !search.value ||
            textSearch(data.nombre, search.value) ||
            textSearch(data.descripcion, search.value) ||
            textSearch(data.modalidad, search.value)
        );

      if (
        selectedYear.value ===
        t("tabla.serviciosPrestadosListado.anoEjercicioTodos")
      ) {
        return filteredBySearch;
      } else {
        // Filtrar por año
        const filteredByYear = filterByYear(selectedYear.value);

        // Intersección de registros filtrados por búsqueda y por año
        return filteredBySearch?.filter((data) =>
          filteredByYear?.includes(data)
        );
      }
    });

    const filterByYear = function (year: string | number) {
      return cursos.value?.filter((data) => {
        const inicio = new Date(data.inicio);
        const fin = new Date(data.fin);
        const inicioYear = inicio.getFullYear();
        const finYear = fin.getFullYear();
        return +year >= inicioYear && +year <= finYear;
      });
    };

    const { result, currentPage, lastPage } = useArrayPagination(
      cursosListado,
      { pageSize: 100 }
    );

    const unsetPage = () => {
      if (currentPage) {
        currentPage.value = 1;
      }
    };

    watch(result, () => {
      unsetPage();
    });

    return {
      cursosListado,
      filteredCollection,
      csvLabels,
      csvFields,
      csvName,
      currentYear,
      selectedYear,
      years,
      handleEdit,
      search,
      toLocaleDateString,
      sortChange: ({ column, prop, order }) => {
        sortProps.value = { prop, order, method: column.sortMethod };
      },
      result,
      currentPage,
      lastPage,
      handleCurrentPageChange: (page: number) => (currentPage.value = page),
    };
  },
});
